import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActiveDescendantKeyManager, FocusKeyManager, ListKeyManager } from '@angular/cdk/a11y';
import { ENTER } from '@angular/cdk/keycodes';
import { FormControl } from '@angular/forms';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { concat, Observable, of } from 'rxjs';
import { FilterSelectOption } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-select.model';
import { NimbusFilterSelectListItemComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-select-list-item/nimbus-filter-select-list-item.component';
import { WindowEventsService } from '@nimbus-library/features/shared/services/window-events.service';
import { FilterSearchOption } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-search.model';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-filter-search-list',
  templateUrl: './nimbus-filter-search-list.component.html',
  styleUrls: ['./nimbus-filter-search-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusFilterSearchListComponent implements AfterViewInit {
  @ViewChildren(NimbusFilterSelectListItemComponent) public items: QueryList<NimbusFilterSelectListItemComponent>;
  @ViewChild('searchInput') public searchInput: ElementRef<HTMLInputElement>;
  private keyManager: ListKeyManager<NimbusFilterSelectListItemComponent>;
  public selectedOptionsLookup: Record<string, boolean> = {};
  public hasSelection: boolean = false;
  public selection: FilterSelectOption[];

  public searchControl: FormControl<string> = new FormControl<string>('');

  public trackByFn = (index: number, item: FilterSelectOption) => item.id;
  @Input() public optionsFn: (search: string) => Observable<FilterSearchOption[]>;
  @Input() public set selectedOptions(val: FilterSelectOption[]) {
    this.selectedOptionsLookup = val.reduce((acc, curr) => {
      acc[curr.id] = true;
      return acc;
    }, {});
    this.selection = val;
    this.hasSelection = val.length > 0;
  }
  @Input() public focusable: boolean = true;
  @Input() public typeAheadEnabled: boolean = true;
  @Output() public selectionChanged: EventEmitter<FilterSelectOption[]> = new EventEmitter();
  @HostListener('keydown', ['$event'])
  public manage(event: KeyboardEvent): void {
    this.keyManager.onKeydown(event);
  }

  public ICONS = ICONS;
  public TranslationKeys = TranslationKeys;
  public options$: Observable<FilterSelectOption[]>;

  public constructor(private readonly windowEventsService: WindowEventsService) {}

  public ngAfterViewInit(): void {
    const keyManagerBase = (this.focusable ? new FocusKeyManager(this.items) : new ActiveDescendantKeyManager(this.items)).withWrap();
    this.keyManager = this.typeAheadEnabled ? keyManagerBase.withTypeAhead() : keyManagerBase;

    this.windowEventsService
      .getKeyDown$()
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        this.keyManager.onKeydown(event);
        if (event.keyCode === ENTER && this.keyManager.activeItem) {
          this.keyManager.activeItem.setClickStyle();
        }
      });

    this.windowEventsService
      .getKeyUp$()
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        if (event.keyCode === ENTER && this.keyManager.activeItem) {
          this.keyManager.activeItem.onClick();
        }
      });

    this.options$ = concat(
      of([]),
      this.searchControl.valueChanges.pipe(
        distinctUntilChanged(),
        switchMap((value) => {
          const phoneSearch = value?.startsWith('+');
          const limit = phoneSearch ? 6 : 3;

          return value?.length >= limit ? this.optionsFn(value) : of([]);
        }),
        map((options) => options.filter((option) => !this.selectedOptionsLookup[option.id])),
      ),
    ).pipe(untilDestroyed(this));

    this.searchInput?.nativeElement?.focus();
  }

  public toggleSelection(availableOptions: FilterSearchOption[]) {
    if (this.hasSelection) {
      this.clearAll();
    } else {
      this.selectAll(availableOptions);
    }
  }

  public onFilterSelect({ selected, option }: { selected: boolean; option: FilterSelectOption }) {
    if (selected) {
      this.selectionChanged.emit([...this.selection, option]);
    } else {
      this.selectionChanged.emit(this.selection.filter((selectedOption) => selectedOption.id !== option.id));
    }
  }

  public onRemoveSearch(): void {
    this.searchControl.setValue(null);
  }

  private selectAll(selection: FilterSelectOption[]) {
    this.selectionChanged.emit(selection);
  }

  private clearAll() {
    this.selectionChanged.emit([]);
  }
}
